import React, { useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateassetReqMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import nameStringFn from '../../../../data/nameStringFn';

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const ReturnButton = styled(Button)`
  background-color: #ff9800;
`;

const ForwardButton = styled(Button)`
  background-color: #4caf50;
`;

const ApproveButton = styled(Button)`
  background-color: #2e7d32;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const UnitHeadInboxForm = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');

  const auth = useAuth();
  let user_current_office = (useSelector((state) => state.office.off));

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";

  const [updateReq] = useUpdateassetReqMutation();

  // Check types of requests that can be handled by unit head
  const isForwardedFromBo = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_fwd_unithead';
  const isPartialIssueForward = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_parissue_forapprv';
  const isPartialIssueClose = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_parissueclse_forapprv';
  const isAllIssueClose = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_issueallclose_forapprv';

  // Get L1ReqApprover of the requested store
  const getStoreLevel1Approver = () => {
    if (!row?.req_store || !storeoffices) return null;
    
    const storeOffice = Object.values(storeoffices).find(store => 
      store.storeoffice === row.req_store
    );
    
    return storeOffice?.roleUsers?.L1ReqApprover_A || null;
  };

  // Handler for forwarding to store
  const handleForwardToStore = async () => {
    const storeLevel1Id = getStoreLevel1Approver();
    
    if (!storeLevel1Id) {
      alert("Store level 1 approver not found. Please ensure the store is properly configured.");
      return;
    }

    let senttoData = {
      sent_to_tag: "uhreq_fwded_store",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
      sent_to: { id: storeLevel1Id, name: getSingleuser?.[storeLevel1Id]?.name, office: row?.req_store, designation: "Level1Manager" },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeLevel1Id])}-${row?.req_store}-Level1Manager`,
      time_stamp: new Date(),
      action:'Request Forwarded to Store',
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Request forwarded to store successfully");
      handleBack();
    } catch (error) {
      console.error("Error forwarding request:", error);
      alert("There was an error forwarding the request. Please try again.");
    }
  };

  // Handler for returning to BO for modification
  const handleReturnToBo = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for returning the request.");
      return;
    }

    let senttoData = {
      sent_to_tag: "uhreq_bolvl_rtrned_mod",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
      sent_to: { id: BO_id, name: getSingleuser?.[BO_id]?.name, office: user_current_office, designation: getSingleuser?.[BO_id].designation?.[user_current_office] },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[BO_id])}-${user_current_office}-${getSingleuser?.[BO_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date(),
      action: 'Request Returned to BO for modification',
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Request returned to BO for modification");
      handleBack();
    } catch (error) {
      console.error("Error returning request:", error);
      alert("There was an error returning the request. Please try again.");
    }
  };

  // Handler for approving partial request for forwarding to store
  const handleApprovePartialForStore = async () => {
    let senttoData = {
      sent_to_tag: "uhreq_paraprvd_forstore",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
      sent_to: { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date(),
      action: 'Partial Issue Approved for Forwarding to Store',
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Partial issue approved for user to accept before forwarding to store");
      handleBack();
    } catch (error) {
      console.error("Error approving request:", error);
      alert("There was an error approving the request. Please try again.");
    }
  };

  // Handler for approving partial request for closing
  const handleApprovePartialForClose = async () => {
    let senttoData = {
      sent_to_tag: "uhreq_paraprvd_forclose",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
      sent_to: { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date(),
      action: 'Partial Issue Approved for Closing',
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Partial issue approved for user to accept and close the request");
      handleBack();
    } catch (error) {
      console.error("Error approving request:", error);
      alert("There was an error approving the request. Please try again.");
    }
  };

  // Handler for approving all assets issued at BO level
  const handleApproveAllAssetsBoLevel = async () => {
    let senttoData = {
      sent_to_tag: "uhreq_allbolvlaprvd_forclose",
      Text: remark,
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: auth?.designation?.[user_current_office] },
      sent_to: { id: row?.req_creator_id, name: getSingleuser?.[row?.req_creator_id]?.name, office: row?.req_data[0]?.sent_by?.office, designation: row?.req_data[0]?.sent_by?.designation },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[row?.req_creator_id])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[row?.req_creator_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date(),
      action: 'All Assets Issued at BO Level Approved for User to Accept',
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("All assets issued at BO level approved for user to accept");
      handleBack();
    } catch (error) {
      console.error("Error approving request:", error);
      alert("There was an error approving the request. Please try again.");
    }
  };

  return (
    <>
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>

      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {isForwardedFromBo && (
          <>
            <ForwardButton onClick={handleForwardToStore}>
              Forward to Store
            </ForwardButton>
            <ReturnButton onClick={handleReturnToBo}>
              Return to BO for Modification
            </ReturnButton>
          </>
        )}

        {isPartialIssueForward && (
          <>
            <ApproveButton onClick={handleApprovePartialForStore}>
              Approve Partial Request for Store
            </ApproveButton>
            <ReturnButton onClick={handleReturnToBo}>
              Return to BO for Modification
            </ReturnButton>
          </>
        )}

        {isPartialIssueClose && (
          <>
            <ApproveButton onClick={handleApprovePartialForClose}>
              Approve Partial Request and Close
            </ApproveButton>
            <ReturnButton onClick={handleReturnToBo}>
              Return to BO for Modification
            </ReturnButton>
          </>
        )}

        {isAllIssueClose && (
          <>
            <ApproveButton onClick={handleApproveAllAssetsBoLevel}>
              Approve Request
            </ApproveButton>
            <ReturnButton onClick={handleReturnToBo}>
              Return to BO for Modification
            </ReturnButton>
          </>
        )}
      </div>
    </>
  );
};

export default UnitHeadInboxForm; 